import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchUserWorkgroups,
  updateUserPreferences,
  getUserPreferences,
  createPromptCard,
  fetchPromptGroups,
  addCardToGroup,
  initializeDefaultPromptGroups
} from "./firestoreFunctions";
import '../styles/Dashboard.css';
import '../styles/ChatLayout.css'; // Import our new ChatGPT-style layout CSS
import '../styles/InputHighlight.css'; // Import the input highlight CSS
import PromptManager from './PromptManager'; // Import the PromptManager component
import interact from 'interactjs'
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { Settings } from './Settings';
import { WorkgroupSelect } from './WorkgroupSelect';
import { Sidebar } from './Sidebar';
import { LoadingIndicator } from './LoadingIndicator';
import { InternalContent } from './InternalContent';
import { GripVertical, PanelLeft, PanelLeftClose, Plus, HelpCircle } from 'lucide-react';
import { getToken } from '../firebase'
import { getModelDisplayName } from '../utils/modelNames';

const llmUrl = `${location.protocol}//${location.hostname}/api/llm`
console.log("llmUrl", llmUrl)
console.log("process.env.REACT_APP_LLM_URL", process.env.REACT_APP_LLM_URL)

const Dashboard = ({ user, onLogout }) => {
  const [workgroups, setWorkgroups] = useState([]);
  const [selectedWorkgroupId, setSelectedWorkgroupId] = useState('');
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [sessions, setSessions] = useState([]);  // Add state to store sessions
  const [first_load, setFirstLoad] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState(false); // Add loading state
  const [isSmallScreen, setSmallScreen] = useState(window.innerWidth < 670);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 670);
  const [internal_messages, setInternalMessages] = useState([])
  const [isDev, setIsDev] = useState(false)
  const [showInternalContent, setShowInternalContent] = useState(false);
  const [systemPromptInput, setSystemPromptInput] = useState('')
  const [attachments, setAttachments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [internalContentWidth, setInternalContentWidth] = useState(400);
  const [sendKey, setSendKey] = useState('Enter'); // New state for send key preference
  const [status, setStatus] = useState(''); // State to store status from SSE

  // State for saving prompts functionality
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [promptToPromote, setPromptToPromote] = useState({
    query: '',
    title: '',
    isWorkgroup: true,
    groupId: ''
  });
  const [promptGroups, setPromptGroups] = useState([]);

  const [theme, setTheme] = useState('light');
  const [modelSelection, setModelSelection] = useState(0);
  const [maxModelOptions, setMaxModelOptions] = useState(0);
  const [ipProtection, setIpProtection] = useState(true);
  const [queryMode, setQueryMode] = useState('standard'); // 'standard' or 'direct' (RAG-only)
  const [highlightInput, setHighlightInput] = useState(false);
  const [modelList, setModelList] = useState([]); // Add state for model list
  const resolveModelName = (index) => {
    const entry = modelList[index];
    const providedName =
      typeof entry === 'string'
        ? entry
        : entry && typeof entry === 'object'
          ? entry.name
          : undefined;
    return getModelDisplayName(index, providedName);
  };


  const examples = [
    { title: "What is my most valuable opportunity?", description: "What is my most valuable opportunity" },
    { title: "Who haven't I contacted this month?", description: "" },
    { title: "Which campaigns have worked best?", description: "" },
    { title: "What department has the most staff members?", description: "" },
  ];

  // React ref to persist sessionId across renders
  const sessionIdRef = useRef(null);
  const chatContainerRef = useRef(null);
  const sidebarRef = useRef(null); // Ref for the sidebar
  const inputRef = useRef(null); // Ref for chat input to focus

  const navigate = useNavigate();

  // Debug helper to inspect layout positioning when messages/state change
  const debugLayout = (tag) => {
    try {
      const html = document.documentElement;
      const shell = document.querySelector('.main-shell');
      const spacer = document.getElementById('topnav-spacer');
      const topnavVar = getComputedStyle(html).getPropertyValue('--topnav-height').trim();
      const shellTop = shell ? getComputedStyle(shell).top : 'n/a';
      const spacerH = spacer ? spacer.style.height : 'n/a';
      const header = document.querySelector('.chat-header');
      const messagesEl = document.querySelector('.chat-messages');
      let msgScrollTop = 'n/a';
      let msgClientTop = 'n/a';
      let headerClientTop = 'n/a';
      if (messagesEl) {
        const rect = messagesEl.getBoundingClientRect();
        msgClientTop = rect.top;
        if (messagesEl instanceof HTMLElement) {
          msgScrollTop = messagesEl.scrollTop;
        }
      }
      if (header) {
        headerClientTop = header.getBoundingClientRect().top;
      }
      console.log(`[LayoutDebug:${tag}]`, {
        topnavVar,
        spacerH,
        shellTop,
        msgScrollTop,
        msgClientTop,
        headerClientTop,
        winScrollY: window.scrollY,
      });
    } catch (e) {
      console.log('LayoutDebug error', e);
    }
  };

  // Defensive: ensure the app shell remains offset only if it's fixed-positioned.
  // With the current layout, .main-shell is relative and should NOT have a top offset.
  const enforceShellTop = () => {
    try {
      const shell = document.querySelector('.main-shell');
      if (!shell) return;
      const style = getComputedStyle(shell);
      const pos = style.position;
      if (pos === 'fixed') {
        const spacer = document.getElementById('topnav-spacer');
        const topNow = style.top;
        const expected = spacer?.style.height || getComputedStyle(document.documentElement).getPropertyValue('--topnav-height').trim();
        if (topNow === '0px' && expected && shell instanceof HTMLElement) {
          shell.style.top = expected;
          console.log('[LayoutGuard] Corrected .main-shell top from 0px ->', expected);
        }
      } else {
        // If not fixed, clear any inline top that might create a gap under the TopNav
        if (shell instanceof HTMLElement && shell.style.top) {
          shell.style.top = '';
          console.log('[LayoutGuard] Cleared .main-shell inline top for non-fixed layout');
        }
      }
    } catch {}
  };

  // Fetch workgroups when the user logs in
  useEffect(() => {
    const fetchWorkgroups = async () => {
      try {
        const userWorkgroups = await fetchUserWorkgroups(user.uid);
        if (userWorkgroups.length > 0) {
          // Fetch workgroup details for each ID
          const workgroupObjs = [];
          for (const id of userWorkgroups) {
            workgroupObjs.push({ id, name: id });
              
          }
          setWorkgroups(workgroupObjs);
          console.log(`Work groups: ${workgroupObjs.map(wg => wg.name).join(', ')}`);
          const storedWorkgroup = localStorage.getItem('selectedWorkgroupId');
          const storedSessionId = localStorage.getItem('sessionId');
          const autoRecover = localStorage.getItem('autoRecoverOnLoad') === 'true';
          if (storedWorkgroup && workgroupObjs.some(wg => wg.id === storedWorkgroup)) {
            setSelectedWorkgroupId(storedWorkgroup);
            // Only recover chat on load if explicitly opted-in
            if (autoRecover && storedSessionId) {
              console.log(`Auto-recover enabled. Stored session = ${storedSessionId}`)
              sessionIdRef.current = storedSessionId;  // Set sessionIdRef
              try {
                await recoverChat(sessionIdRef.current, user.uid, selectedWorkgroupId);
              } catch (err) {
                sessionIdRef.current = null
                console.log("Failed to recover chat")
              }
            } else {
              // Default behavior: start fresh on Home without auto-loading previous messages
              sessionIdRef.current = null
              localStorage.removeItem('sessionId');
            }
            if (sessionIdRef.current == null) {
              console.log("Making session on workgroup fetch")
              setSelectedWorkgroupId(storedWorkgroup);
              // Try AWS config first, then fall back to Salesforce
              const tryConnection = async () => {
                try {
                  await initializeConnection()
                } catch (error) {
                  console.error("Error fetching connection config", error);
                }
              };

              tryConnection();
            }
          } else {
            setSelectedWorkgroupId(workgroupObjs[0].id);
          }
        } else {
          setWorkgroups([]); // No workgroups found
        }
      } catch (err) {
        setError("Failed to fetch workgroups.");
      }
    };
    try {
      fetchWorkgroups();
    } catch (err) {
      setError("Could not fetch workgroups", err);
    }
  }, [user.uid]);

  // Start a new session if the selected workgroup changes
  useEffect(() => {
    if (first_load) {
      console.log("Workgroup change effect: Skipping first load");
      setFirstLoad(false);
      return;
    }
    if (!selectedWorkgroupId) {
      console.log("Workgroup change effect: No workgroup selected, skipping");
      return;
    }
    const storedWorkgroup = localStorage.getItem('selectedWorkgroupId');
    console.log("Workgroup change effect: Current workgroup:", selectedWorkgroupId, "Stored workgroup:", storedWorkgroup);

    if (storedWorkgroup == selectedWorkgroupId) {
      console.log("Workgroup change effect: Workgroup didn't change, skipping new session");
      return;
    }

    console.log("Workgroup change effect: Making new session");
    sessionIdRef.current = null;  // Reset sessionIdRef
    localStorage.removeItem('sessionId');  // Remove sessionId from localStorage
    initializeConnection();
  }, [selectedWorkgroupId]);

  // Fetch sessions when workgroup changes
  useEffect(() => {
    console.log("Sessions effect triggered. Current workgroup:", selectedWorkgroupId);
    if (selectedWorkgroupId) {
      console.log("Sessions effect: Calling fetchSessions for workgroup:", selectedWorkgroupId);
      fetchSessions();
    } else {
      console.log("Sessions effect: No workgroup selected, skipping fetch");
    }
  }, [selectedWorkgroupId]);

  // Persist the sessionId and workgroup to localStorage
  useEffect(() => {
    if (selectedWorkgroupId) {
      console.log("Persisting workgroup to localStorage:", selectedWorkgroupId);
      localStorage.setItem('selectedWorkgroupId', selectedWorkgroupId);
    }
  }, [selectedWorkgroupId]);
  const fetchSessions = async () => {
    try {
      console.log("fetchSessions: Starting fetch for workgroup:", selectedWorkgroupId);
      const token = await getToken();
      const url = `${llmUrl}/v1/getLastSessions?user_id=${user.uid}&workgroup=${selectedWorkgroupId}&token=${token}`;
      console.log("fetchSessions: Fetching from URL:", url);
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Error fetching sessions: ${response.status}`);
      }
      const data = await response.json();
      
      // Log the raw data structure
      console.log("fetchSessions: Raw data structure:", JSON.stringify(data, null, 2));
      
      if (data.sessions && data.sessions.length > 0) {
        // Log the complete structure of the first session
        console.log("fetchSessions: Complete structure of first session:", JSON.stringify(data.sessions[0], null, 2));
        console.log("fetchSessions: Available fields on first session:", Object.keys(data.sessions[0]));
        
        // Check if workgroup information might be nested
        const firstSession = data.sessions[0];
        console.log("fetchSessions: Checking for nested workgroup info:", {
          hasMetadata: !!firstSession.metadata,
          hasConfig: !!firstSession.config,
          hasSettings: !!firstSession.settings
        });
        
        // Set sessions without filtering for now
        setSessions(data.sessions);
        return data.sessions;
      }
      console.log("fetchSessions: No sessions in response, setting empty array");
      setSessions([]);
      return [];
    } catch (error) {
      console.error("fetchSessions: Error:", error);
      setSessions([]);
      return [];
    }
  };

  useEffect(() => {
    const setupPreferences = async () => {
      const preferences = await getUserPreferences(user.uid)
      console.log(preferences)
      if (preferences) {
        handleThemeChange(preferences.theme || 'light', false)
        setSendKey(preferences.sendKey || 'Enter')
        if (preferences.modelSelection !== undefined) {
          setModelSelection(preferences.modelSelection)
        }
        setIpProtection(preferences.ipProtection !== false) // Default to true unless explicitly set to false
      } else {
        // Default values if preferences is null
        handleThemeChange('light', false)
        setSendKey('Enter')
        setIpProtection(true)
      }
    }
    setupPreferences()
  }, [user.uid])

  // Modify the existing useEffect that fetches num options to also fetch model list
  useEffect(() => {
    const fetchNumOptions = async () => {
      try {
        const token = await getToken();
        const response = await fetch(`${llmUrl}/v1/getNumOptions?token=${token}`);

        if (!response.ok) {
          throw new Error(`Error fetching options: ${response.status}`);
        }

        const data = await response.json();
        if (data.num_options) {
          setMaxModelOptions(data.num_options);
        }
      } catch (error) {
        console.error("Error fetching number of options:", error);
        // Set a default value if API call fails
        setMaxModelOptions(3);
      }
    };

    fetchNumOptions();
  }, []); // Remove isDev dependency

  // Add separate useEffect for fetching model list
  useEffect(() => {
    if (isDev) {
      fetchModelList();
    } else {
      setModelList([]); // Clear model list when not in dev mode
    }
  }, [isDev]); // Only depend on isDev

  // State for Android navigation bar detection
  const [hasAndroidNavBar, setHasAndroidNavBar] = useState(false);
  const isAndroid = useRef(navigator.userAgent.toLowerCase().includes('android'));

  // Effect to add event listener for window resize and detect Android system navigation
  useEffect(() => {
    const handleResize = () => {
      const smallScreen = window.innerWidth < 670;
      setSmallScreen(smallScreen);
      // Don't automatically change sidebar state on resize
      // to respect user preference on all screen sizes

      // Check for Android system navigation bar
      if (isAndroid.current && window.visualViewport) {
        // On Android, if visual viewport height is significantly less than window inner height,
        // it likely means system navigation bar is present
        const heightDiff = window.innerHeight - window.visualViewport.height;

        // Typical Android navigation bar is 48dp in height
        // We use a threshold of 30px to account for slight variations
        if (heightDiff > 30) {
          console.log('Android system navigation bar detected, height diff:', heightDiff);
          setHasAndroidNavBar(true);
          document.documentElement.classList.add('android-nav-present');
        } else {
          setHasAndroidNavBar(false);
          document.documentElement.classList.remove('android-nav-present');
        }
      }
    };

    handleResize(); // Set initial screen size detection
    window.addEventListener('resize', handleResize);

    // Listen for visualViewport resize events on supported browsers
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  // Effect to close sidebar on outside click - only on mobile screens
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Skip if clicking the hamburger menu button
      if (event.target.closest('[data-hamburger-menu]')) {
        return;
      }

      // Only close sidebar on outside click for mobile screens
      if (window.innerWidth < 670 && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsSidebarOpen(false);
      }
    };

    if (isSidebarOpen && isSmallScreen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSidebarOpen, isSmallScreen]);

  // Effect to handle swipe gestures
  useEffect(() => {
    let touchStartX = 0;
    let touchEndX = 0;

    const handleTouchStart = (event) => {
      // Don't capture touch events when interacting with scrollable content
      if (event.target.closest('.overflow-x-auto, .messages-container, table, [role="grid"]')) {
        return;
      }
      touchStartX = event.changedTouches[0].screenX;
    };

    const handleTouchEnd = (event) => {
      // Don't process swipe if we didn't capture start
      if (touchStartX === 0) return;

      // Don't capture touch events when interacting with scrollable content
      if (event.target.closest('.overflow-x-auto, .messages-container, table, [role="grid"]')) {
        return;
      }

      touchEndX = event.changedTouches[0].screenX;
      handleGesture();

      // Reset touch positions
      touchStartX = 0;
      touchEndX = 0;
    };

    const handleGesture = () => {
      // Increase threshold for more intentional swipes
      if (touchStartX - touchEndX > 100) {
        setIsSidebarOpen(false); // Close sidebar on left swipe
      } else if (touchEndX - touchStartX > 100) {
        setIsSidebarOpen(true); // Open sidebar on right swipe
      }
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  // Use useEffect to scroll to the bottom when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
    debugLayout('messages-change');
    enforceShellTop();
  }, [messages]);  // Trigger scroll when messages change

  // Focus input field on initial load
  useEffect(() => {
    // Short delay to ensure component is fully rendered
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }

      // Show highlight animation
      setHighlightInput(true);
      setTimeout(() => {
        setHighlightInput(false);
      }, 2000); // Keep highlight for 2 seconds
    }, 100);

    return () => clearTimeout(timer);
  }, []); // Empty dependency array means this runs once on mount

  // Apply highlight class to input field when highlightInput changes
  useEffect(() => {
    // Find the textarea inside the chat input
    const inputContainer = document.querySelector('.chat-input-container');
    if (inputContainer) {
      const textarea = inputContainer.querySelector('textarea');
      const inputWrapper = textarea?.closest('div.flex.items-center.bg-white.rounded-xl.border') || null;

      if (inputWrapper) {
        if (highlightInput) {
          inputWrapper.classList.add('highlight-border');
        } else {
          inputWrapper.classList.remove('highlight-border');
        }
      }
    }
  }, [highlightInput]);

  // Log when loading indicator toggles (often aligns with AI responses)
  useEffect(() => {
    debugLayout(`loadingMessage:${loadingMessage}`);
    enforceShellTop();
  }, [loadingMessage]);

  useEffect(() => {
    enforceShellTop();
    const onResize = () => enforceShellTop();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Listen for confirmation response event from ChatMessage component
  useEffect(() => {
    const handleConfirmationSent = (event) => {
      console.log('Confirmation sent:', event.detail);
      const { question, actionType, response, sessionId } = event.detail;

      // Check if this is for the current session
      if (sessionId === sessionIdRef.current) {
        // Format the action type to be more readable
        let formattedAction = actionType.replace(/_/g, ' ');

        // Handle common action types with special formatting
        if (actionType === 'api_call') {
          formattedAction = 'proceeding with the requested action';
        } else if (actionType.includes('_')) {
          // Convert snake_case to sentence case
          formattedAction = actionType
            .replace(/_/g, ' ')
            .replace(/\b\w/g, c => c.toUpperCase())
            .replace(/Api/g, 'API');
        }

        // Add the user's confirmation to the chat
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            id: prevMessages.length,
            content: `${response === 'yes' || response === 'yes_remember' ? 'Yes' : 'No'}, ${response === 'yes_remember' ? "don't ask again. " : ""}${formattedAction}.`,
            role: "user",
            timestamp: Date.now()
          },
        ]);

        // Set loading indicator
        setLoadingMessage(true);
      }
    };

    const handleConfirmationResponse = (event) => {
      console.log('Confirmation response received:', event.detail);
      const { assistantResponse, sessionId } = event.detail;

      // Check if this is for the current session
      if (sessionId === sessionIdRef.current) {
        // Hide loading indicator
        setLoadingMessage(false);

        // Add the assistant's response to the chat
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            id: prevMessages.length,
            content: assistantResponse,
            role: "assistant",
            timestamp: Date.now()
          },
        ]);

        // Send the message with the current modelSelection
        sendMessage(assistantResponse, [], [], modelSelection);
      }
    };

    // New handler for direct message sending from ChatMessage
    const handleSendMessage = (event) => {
      console.log('Send message event received:', event.detail);
      const { message, sessionId } = event.detail;

      // Check if this is for the current session
      if (sessionId === sessionIdRef.current) {
        // Use the existing sendMessage function
        sendMessage(message);
      }
    };

    window.addEventListener('confirmation-sent', handleConfirmationSent);
    window.addEventListener('confirmation-response-received', handleConfirmationResponse);
    window.addEventListener('send-message', handleSendMessage);

    return () => {
      window.removeEventListener('confirmation-sent', handleConfirmationSent);
      window.removeEventListener('confirmation-response-received', handleConfirmationResponse);
      window.removeEventListener('send-message', handleSendMessage);
    };
  }, [modelSelection]);

  useEffect(() => {
    interact('.resizable')
      .resizable({
        edges: { top: false, left: true, bottom: false, right: true },
        listeners: {
          move: function (event) {
            let { x, y } = event.target.dataset;

            x = (parseFloat(x) || 0) + event.deltaRect.left;
            y = (parseFloat(y) || 0) + event.deltaRect.top;

            Object.assign(event.target.style, {
              width: `${event.rect.width}px`,
              height: `${event.rect.height}px`,
            });

            Object.assign(event.target.dataset, { x, y });
          },
        },
      });
  }, []); // Empty dependency array ensures this runs once after the component is mounted.

  // Effect to handle orientation changes on Android
  useEffect(() => {
    if (isAndroid.current) {
      const handleOrientationChange = () => {
        console.log('Orientation changed, checking for Android navigation bar');

        // Small delay to let the browser adjust after orientation change
        setTimeout(() => {
          if (window.visualViewport) {
            const heightDiff = window.innerHeight - window.visualViewport.height;

            if (heightDiff > 30) {
              console.log('Android system navigation bar detected after orientation change, height diff:', heightDiff);
              setHasAndroidNavBar(true);
              document.documentElement.classList.add('android-nav-present');
            } else {
              setHasAndroidNavBar(false);
              document.documentElement.classList.remove('android-nav-present');
            }
          } else {
            // Fallback method for devices without visualViewport API
            const aspectRatio = window.innerWidth / window.innerHeight;
            if (aspectRatio < 0.6) {
              // Very tall device, likely has navigation bar
              document.documentElement.classList.add('android-nav-present');
              setHasAndroidNavBar(true);
            } else {
              document.documentElement.classList.remove('android-nav-present');
              setHasAndroidNavBar(false);
            }
          }
        }, 500);
      };

      // Listen for both orientationchange and resize events
      window.addEventListener('orientationchange', handleOrientationChange);

      return () => {
        window.removeEventListener('orientationchange', handleOrientationChange);
      };
    }
  }, []);

  useEffect(() => {
    if (!internal_messages[0]) { }
    else {
      setSystemPromptInput(internal_messages[0].content)
    }
  }, [internal_messages])

  // Handle screen resize event to check screen size
  const handleResize = () => {
    setSmallScreen(window.innerWidth <= 670);
  };


  // Initialize connection to backend with workgroup ID
  const initializeConnection = async () => {
    if (!sessionIdRef.current) {
      console.log("No current session ID");

      let token;
      try {
        token = await getToken();
      } catch (error) {
        console.error("Error getting token:", error);
        return;
      }

      var workgroup;
      if (!selectedWorkgroupId) {
        workgroup = localStorage.getItem('selectedWorkgroupId');
      } else {
        workgroup = selectedWorkgroupId;
      }

      if (!workgroup) {
        console.error("No workgroup available for connection");
        return;
      }

      try {
        // Prepare connection payload with only the required fields
        const payload = {
          token: token,
          workgroup: workgroup,
          ip_protection: ipProtection,
          integration_ids: ["default-integration"] // TODO: replace with real selection
        };

        console.log(`Initializing connection for workgroup ${workgroup}`);

        const response = await fetch(`${llmUrl}/v1/startConnection`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          if (response.status === 401) {
            console.error("Authentication error:", response.statusText);
            alert("Credentials not valid, could not create session");
          } else {
            console.error(`Server error (${response.status}):`, response.statusText);
            alert(`Error connecting to server: ${response.statusText}`);
          }
          return;
        }

        try {
          const data = await response.json();
          if (data.session_id) {
            sessionIdRef.current = data.session_id;  // Set sessionIdRef
            localStorage.setItem('sessionId', sessionIdRef.current);
            console.log(`Session created: ${data.session_id}`);
          } else {
            console.error("Session ID not returned from server.");
            alert("Error creating session: No session ID returned");
          }

          if (data.dev) {
            setIsDev(true)
          } else {
            setIsDev(false)
          }
        } catch (jsonError) {
          console.error("Error parsing server response:", jsonError);
          alert("Error parsing server response");
        }
      } catch (error) {
        console.error("Error initializing connection:", error);
        alert("Failed to connect to the server. Please try again later.");
      }
    } else {
      console.log(`Session already exists: ${sessionIdRef.current}`);
    }
  };

  const handleThemeChange = (newTheme, update = true) => {
    setTheme(newTheme);
    if (update) {
      updateUserPreferences(user.uid, newTheme, sendKey, modelSelection, ipProtection)
    }
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  const handleSendKeyChange = (newSendKey) => {
    setSendKey(newSendKey)
    updateUserPreferences(user.uid, theme, newSendKey, modelSelection, ipProtection)
  }

  const handleModelSelectionChange = (newModelSelection) => {
    setModelSelection(newModelSelection)
    updateUserPreferences(user.uid, theme, sendKey, newModelSelection, ipProtection)
  }

  const handleToggleIpProtection = () => {
    const newIpProtection = !ipProtection;
    setIpProtection(newIpProtection);
    updateUserPreferences(user.uid, theme, sendKey, modelSelection, newIpProtection);
  }

  // Recover chat history using sessionId with a GET request
  const recoverChat = async (sessionId, userId, workgroupId) => {
    console.log(`recoverChat: Starting with sessionId=${sessionId}, userId=${userId}, workgroupId=${workgroupId}`);

    if (!sessionId) {
      console.error("recoverChat: sessionId is empty or null");
      throw new Error("Cannot recover chat with empty session ID");
    }

    const token = await getToken();
    console.log("recoverChat: Token obtained, making API request...");

    try {
      // API call to recover the chat session
      const url = `${llmUrl}/v1/recoverChat?session_id=${sessionId}&user_id=${userId}&token=${token}`;
      console.log(`recoverChat: Fetching from URL: ${url}`);

      const response = await fetch(url, {
        method: 'GET',
      });

      console.log(`recoverChat: Response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        throw new Error(`Failed to recover chat: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("recoverChat: Data received from server:", data);

      // Check if the session was found
      if (data.error) {
        console.error(data.error);
        localStorage.removeItem('sessionId');
        return;
      }

      // Extract chat history from the API response
      const chatHistory = data.chat_history;

      // Process and render the recovered chat data using the renderContent function

      const formattedMessages = chatHistory.map((msg, index) => {

        // Attempt to fix array data in tables that got serialized improperly
        const isTableMessage =
          (typeof msg.content === 'string' && msg.content.includes('"type":"table"')) ||
          (Array.isArray(msg.content) && msg.content.some(item => item.type === 'table'));

        let fixedContent = msg.content;
        let fixedSpecialBlocks = msg.specialBlocks;

        // Fix tables and confirmation prompts in content array
        if (Array.isArray(msg.content)) {
          const contentWithFixedItems = msg.content.map(item => {
            // If it's a table item, check if its rows need fixing
            if (item.type === 'table' && item.table && Array.isArray(item.table.rows)) {
              // Identify rows with placeholder values like ["0", "1", "2"]
              const hasPlaceholderRows = item.table.rows.some(row =>
                Array.isArray(row) &&
                row.length > 1 &&
                row.some(val => val === "0" || val === "1" || val === "_length")
              );

              if (hasPlaceholderRows) {
                console.log(`DEBUG: Found placeholder table rows in message content item`);
                // This indicates corrupted data - we can't recover the actual values
                // Mark the table as having data retrieval issues
                return {
                  ...item,
                  table: {
                    ...item.table,
                    _dataRetrievalIssue: true // Flag to indicate data issue
                  }
                };
              }
            }

            // Mark confirmation prompts as already sent when reopening a chat
            if (item.type === 'user_confirm' && item.user_confirm) {
              console.log(`DEBUG: Found confirmation prompt in reopened chat content, marking as already sent`);
              return {
                ...item,
                user_confirm: {
                  ...item.user_confirm,
                  _previouslySent: true // Flag to indicate this confirmation was already acted upon
                }
              };
            }

            return item;
          });

          fixedContent = contentWithFixedItems;
        }

        // Fix specialBlocks tables and mark confirmation prompts as already sent
        if (msg.specialBlocks && msg.specialBlocks.length > 0) {
          // Process each special block to handle tables and confirmations
          const fixedBlocks = msg.specialBlocks.map(block => {
            // Handle tables with data issues
            if (block.type === 'table' && block.content && block.content.rows) {
              // Check for placeholder rows
              const hasPlaceholderRows = block.content.rows.some(row =>
                Array.isArray(row) &&
                row.length > 1 &&
                row.some(val => val === "0" || val === "1" || val === "_length")
              );

              if (hasPlaceholderRows) {
                console.log(`DEBUG: Found placeholder rows in specialBlock table`);
                return {
                  ...block,
                  content: {
                    ...block.content,
                    _dataRetrievalIssue: true // Flag to indicate data issue
                  }
                };
              }
            }

            // Mark confirmation prompts as already sent when reopening a chat
            if (block.type === 'user_confirm') {
              console.log(`DEBUG: Found confirmation prompt in reopened chat, marking as already sent`);
              return {
                ...block,
                content: {
                  ...block.content,
                  _previouslySent: true // Flag to indicate this confirmation was already acted upon
                }
              };
            }

            return block;
          });

          fixedSpecialBlocks = fixedBlocks;
        }

        // Handle string content that might contain serialized tables
        if (typeof msg.content === 'string' && msg.content.includes('"type":"table"')) {
          let parsedContent = null;

          try {
            // Parse the content to fix nested tables
            parsedContent = JSON.parse(msg.content);
          } catch (e) {
            // Try to repair malformed JSON from LLM
            const trimmed = msg.content.trim();

            // Pattern 1a: Multiple objects starting with "type": and containing }, {
            if (trimmed.startsWith('"type"') && trimmed.includes('}, {')) {
              try {
                parsedContent = JSON.parse('[{' + trimmed + ']');
                console.log('DEBUG: Repaired malformed JSON (pattern 1a: multiple objects)');
              } catch (e2) {
                try {
                  parsedContent = JSON.parse('[{' + trimmed + '}]');
                  console.log('DEBUG: Repaired malformed JSON (pattern 1a alt)');
                } catch (e3) {
                  // Continue to next pattern
                }
              }
            }

            // Pattern 1b: Single object starting with "type":
            if (!parsedContent && trimmed.startsWith('"type"')) {
              try {
                parsedContent = JSON.parse('[{' + trimmed + '}]');
                console.log('DEBUG: Repaired malformed JSON (pattern 1b: single object)');
              } catch (e2) {
                // Continue to next pattern
              }
            }

            // Pattern 2: Array items without outer brackets starting with {
            if (!parsedContent && trimmed.startsWith('{') && trimmed.includes('}, {')) {
              try {
                parsedContent = JSON.parse('[' + trimmed + ']');
                console.log('DEBUG: Repaired malformed JSON (pattern 2)');
              } catch (e2) {
                // Repair failed
              }
            }

            if (!parsedContent) {
              console.log(`DEBUG: Failed to parse content for message ${index}:`, e.message);
            }
          }

          if (Array.isArray(parsedContent)) {
            // Process each content item to fix tables
            const fixedItems = parsedContent.map(item => {
              if (item.type === 'table' && item.table && Array.isArray(item.table.rows)) {
                // Check for placeholder rows
                const hasPlaceholderRows = item.table.rows.some(row =>
                  Array.isArray(row) &&
                  row.length > 1 &&
                  row.some(val => val === "0" || val === "1" || val === "_length")
                );

                if (hasPlaceholderRows) {
                  return {
                    ...item,
                    table: {
                      ...item.table,
                      _dataRetrievalIssue: true // Flag for UI to show data issue
                    }
                  };
                }
              }
              return item;
            });

            // Keep as array instead of re-stringifying
            fixedContent = fixedItems;
          }
        }

        return {
          role: msg.role,
          content: fixedContent,
          timestamp: new Date(msg.timestamp),
          id: new Date(msg.timestamp),
          ...(msg.images ? { images: msg.images } : {}),
          ...(fixedSpecialBlocks ? { specialBlocks: fixedSpecialBlocks } : {})
        };
      });

      // Update the state to display the formatted messages
      console.log(`recoverChat: Updating messages with ${formattedMessages.length} messages`);
      setMessages(formattedMessages);
      sessionIdRef.current = sessionId;

      // Set a flag in localStorage to indicate this is a recovered chat
      // This will help us properly handle confirmation prompts
      localStorage.setItem('isRecoveredChat', 'true');
      console.log("recoverChat: Set isRecoveredChat flag in localStorage");

      if (data.dev) {
        console.log("recoverChat: Setting dev mode");
        setIsDev(true);
        setInternalMessages(data.messages);
      } else {
        console.log("recoverChat: Not in dev mode");
        setIsDev(false);
      }

      console.log("recoverChat: Recovery successful");
      return true; // Indicate success

    } catch (error) {
      console.error(`Error recovering chat ${sessionIdRef.current}:`, error);
      sessionIdRef.current = null;
      localStorage.removeItem('sessionId');

      const storedWorkgroup = localStorage.getItem('selectedWorkgroupId');
      console.log(`recoverChat: Recovery failed, falling back to stored workgroup: ${storedWorkgroup}`);

      try {
        // Initialize a new connection
        await initializeConnection();
      } catch (fallbackError) {
        console.error("Failed to initialize new connection after recovery failure:", fallbackError);
        alert("Failed to reconnect after chat recovery failed");
      }

      throw error; // Re-throw to indicate failure
    }
  };


  // Add message deduplication
  const [isProcessingMessage, setIsProcessingMessage] = useState(false);
  const processingTimeoutRef = useRef(null);
  const lastProcessedMessageRef = useRef("");
  const lastProcessedTimeRef = useRef(0);
  const hasShownSeventyPercentAlertRef = useRef(false);
  const requestCancelledRef = useRef(false); // Track if current request was cancelled
  const licensePageUrl = `${window.location.origin}/admin?section=license`;

  const showLicenseToast = (message, variant = 'warning') => {
    try {
      const toast = document.createElement('div');
      toast.className = `fixed bottom-4 left-4 max-w-sm text-sm text-white px-4 py-3 rounded shadow-lg z-[9999] license-limit-toast ${variant === 'danger' ? 'bg-red-600' : 'bg-amber-500'}`;
      toast.style.opacity = '0';
      toast.style.transition = 'opacity 0.3s ease';
      toast.style.pointerEvents = 'auto';
      toast.innerHTML = message;
      document.body.appendChild(toast);
      requestAnimationFrame(() => {
        toast.style.opacity = '1';
      });
      setTimeout(() => {
        toast.style.opacity = '0';
        toast.addEventListener('transitionend', () => {
          if (toast.parentNode) {
            toast.parentNode.removeChild(toast);
          }
        }, { once: true });
      }, 6000);
    } catch (error) {
      console.error("Failed to show license toast", error);
    }
  };

  // Handle sending messages with deduplication
  const sendMessage = async (messageToSend = input, images = attachments, files = [], passedModelSelection) => {
    console.log(`Input = ${input}, Message = ${messageToSend}`);
    // Generate a unique fingerprint for this message
    const currentTime = Date.now();
    const messageFingerprint = typeof messageToSend === 'string' ? messageToSend.trim() : '';

    // Check if this is a duplicate message (same content within 5 seconds)
    if (messageFingerprint === lastProcessedMessageRef.current &&
      (currentTime - lastProcessedTimeRef.current) < 5000) {
      console.log("🛑 DUPLICATE MESSAGE DETECTED - SAME CONTENT WITHIN 5 SECONDS");
      console.log(`Previous: "${lastProcessedMessageRef.current}" (${currentTime - lastProcessedTimeRef.current}ms ago)`);
      console.log(`Current: "${messageFingerprint}"`);
      return false;
    }

    // Prevent concurrent processing
    if (isProcessingMessage) {
      console.log("🛑 Already processing a message, ignoring concurrent request");
      return false;
    }

    // Update tracking variables
    lastProcessedMessageRef.current = messageFingerprint;
    lastProcessedTimeRef.current = currentTime;

    // Set processing flag and clear any existing timeout
    setIsProcessingMessage(true);
    requestCancelledRef.current = false; // Reset cancelled flag for new request
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
    }

    // Set timeout to reset processing flag after 5 seconds
    processingTimeoutRef.current = setTimeout(() => {
      setIsProcessingMessage(false);
    }, 5000);

    if (typeof messageToSend === 'object' && input !== '') {
      console.log("Replacing message with input");
      messageToSend = input;
    }
    try {
      if (!messageToSend.trim()) {
        console.log("Input is empty, message not sent.");
        alert("Message can't be empty");
        setIsProcessingMessage(false);
        console.log("Returning False")
        return false;
      }
    } catch (typeerror) {
      console.log("Input is empty, message not sent.");
      alert("Message can't be empty");
      setIsProcessingMessage(false);
      return false;
    }

    if (!sessionIdRef.current) {
      console.log("No sessionId, attempting to initialize connection...");
      
      try {
        // Try to initialize a connection before sending the message
        await initializeConnection();
        
        // Check if initialization worked
        if (!sessionIdRef.current) {
          alert("Cannot connect to server. Please try refreshing the page.");
          setIsProcessingMessage(false);
          return false;
        }
      } catch (error) {
        console.error("Failed to initialize connection:", error);
        alert("Cannot connect to server. Please try refreshing the page.");
        setIsProcessingMessage(false);
        return false;
      }
    }

    setLoadingMessage(true);

    // Add user's message to chat
    setMessages((prevMessages) => [
      ...prevMessages,
      {
        id: prevMessages.length,
        content: messageToSend,
        role: "user",
        timestamp: Date.now(),
        images: images
      },
    ]);

    setInput('');  // Clear input
    console.log(`Model Selection: ${passedModelSelection !== undefined ? passedModelSelection : modelSelection}`)
    // Send the message to the LLM API
    try {
      // Prepare request payload
      const payload = {
        messages: [{ role: "user", content: messageToSend }],
        session_id: sessionIdRef.current,
        token: await getToken(),
        model_selection: passedModelSelection !== undefined ? passedModelSelection : modelSelection,
        ip_protection: ipProtection,
        mode: queryMode  // 'standard' or 'direct' (RAG-only mode)
      };

      // Only include image data if there are actually images (even for empty array)
      if (images && images.length > 0) {
        payload.num_images = images.length;
        payload.images = images;
      }

      // Only include files if there are any
      if (files && files.length > 0) {
        payload.files = files;
      }
      let eventSource = null;

      try {
        var response = fetch(`${llmUrl}/v1/completions`, { // No await to allow fetching of the status
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Session-Id': sessionIdRef.current,  // Use sessionId from ref
          },
          body: JSON.stringify(payload),
        });
        response.catch(error => {
          console.error("Failed to send message:", error);
          alert("Failed to send message: " + (error.message || error.error || "Unknown error"));
          setLoadingMessage(false);
          setIsProcessingMessage(false);
          if (eventSource) {
            eventSource.close();
          }
          return false;
        });
      } catch (error) {
        console.error("Failed to send message:", error);
        alert("Failed to send message: " + (error.message || error.error || "Unknown error"));
        setLoadingMessage(false);
        setIsProcessingMessage(false);
        if (eventSource) {
          eventSource.close();
        }
        return false;
      }

      try {
        // Set up SSE to get status updates
        const token = await getToken();
        eventSource = new EventSource(`${llmUrl}/getStatus?session_id=${sessionIdRef.current}&token=${token}`);
        eventSource.onmessage = (event) => {
          setStatus(event.data); // Update status with data from SSE
        };
        eventSource.onerror = () => {
          eventSource.close(); // Close the connection on error
        };
      } catch (error) {
        if (eventSource) {
          eventSource.close();
        }
        setStatus("Loading")
        console.error(error)
      }
      response = await response
      setLoadingMessage(false)
      setIsProcessingMessage(false)

      console.log(response)

      if (eventSource) {
        eventSource.close();
      }

      if (!await response.ok) {
        const errorText = await response.text();
        console.error(`Server error: ${response.status}`, errorText);

        if (response.status === 402 || errorText.includes("You have used all your available messages for this period")) {
          showLicenseToast(`This workgroup has used its daily limits. To increase this, visit your <a class="underline font-semibold" target="_blank" rel="noopener noreferrer" href="${licensePageUrl}">license page</a> or speak to your administrator.`, 'danger');
          return false;
        }

        // Check for AWS image upload error
        if (errorText.includes("'AWSAgent' object has no attribute 'uploadImages'")) {
          setLoadingMessage(false);
          alert("AWS integration doesn't currently support image uploads. Please try sending a message without images.");
          return false;
        }

        throw new Error(`Error from server: ${response.status}`);
      }

      const data = await response.json();
      // Only process response if it belongs to the current session
      if (data.session_id && data.session_id !== sessionIdRef.current) {
        console.log(`Ignoring response for stale session ${data.session_id}, current session is ${sessionIdRef.current}`);
        return false;
      }

      const hasOverSeventyPercentLimit = (payload) => {
        if (!payload) {
          return false;
        }
        if (payload.over_seventy_percent_limit) {
          return true;
        }
        if (Array.isArray(payload.messages) && payload.messages.some((msg) => msg?.over_seventy_percent_limit)) {
          return true;
        }
        if (Array.isArray(payload.choices)) {
          return payload.choices.some((choice) => {
            if (!choice) return false;
            if (choice.over_seventy_percent_limit) return true;
            return choice.message?.over_seventy_percent_limit;
          });
        }
        return Boolean(payload.choices?.[0]?.message?.over_seventy_percent_limit);
      };

      if (hasOverSeventyPercentLimit(data) && !hasShownSeventyPercentAlertRef.current) {
        showLicenseToast(`This workgroup has used 70% of its daily limits. To avoid interruptions, visit your <a class="underline font-semibold" target="_blank" rel="noopener noreferrer" href="${licensePageUrl}">license page</a> or speak to your administrator.`);
        hasShownSeventyPercentAlertRef.current = true;
      }

      const llmResponse = data.choices[0].message.content;
      if (data.hasOwnProperty("messages")) {
        setInternalMessages(data.messages)
      }

      // Only process response if request wasn't cancelled
      if (requestCancelledRef.current) {
        console.log("Request was cancelled, ignoring response");
        return false;
      }

      setLoadingMessage(false);

      // Add the LLM's response to chat
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          id: prevMessages.length,
          content: llmResponse,
          role: "assistant",
          timestamp: Date.now()
        },
      ]);

      // Scroll to the top of the new message
      if (chatContainerRef.current) {
        const lastMessageElement = chatContainerRef.current.lastElementChild;
        if (lastMessageElement) {
          lastMessageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }

      // Refresh session list if there are 2 or 6 messages
      if (messages.length === 0 || messages.length === 4) {
        fetchSessions();
      }
    } catch (error) {
      // Don't show error if request was cancelled by user
      if (requestCancelledRef.current) {
        console.log("Request was cancelled, ignoring error");
        return false;
      }
      // Check if it's the AWS image upload error that we've already handled
      if (error.message && error.message.includes("AWS integration doesn't currently support image uploads")) {
        // Error already handled with specific alert
        console.error("AWS image upload error handled:", error);
      } else {
        // Show generic error for other issues
        alert("Failed to send message: " + (error.message || error.error || "Unknown error"));
        console.error("Error sending message:", error);
      }
      return false;
    } finally {
      // Reset processing flag to allow new submissions
      setLoadingMessage(false)
      setIsProcessingMessage(false);
    }
    
    setInput("")
    return true;
  };

  const handleNewChat = async () => {
    await closeSession()
    sessionIdRef.current = "";

    // Clear the recovered chat flag when starting a new chat
    localStorage.removeItem('isRecoveredChat');

    // Try to focus input field immediately and highlight it
    if (inputRef.current) {
      inputRef.current.focus();
    }

    // Show highlight animation
    setHighlightInput(true);
    setTimeout(() => {
      setHighlightInput(false);
    }, 2000); // Keep highlight for 2 seconds

    initializeConnection()
      .then(() => {
        setMessages([]);  // Clear the current chat history
        setError(null);   // Reset any errors
        // On mobile: close sidebar, on desktop: keep sidebar open
        if (isSmallScreen) {
          setIsSidebarOpen(false);
        }

        // Focus the input field after creating a new chat
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 100); // Small delay to ensure component has updated
      })
      .catch(err => {
        alert("Failed to create new chat session.");
        console.error("Error creating new chat session:", err);
      });

    fetchSessions()
  };

  const handleExampleClick = async (command) => {
    setInput(command);
    sendMessage(command);
    // Only close sidebar on mobile when clicking examples
    if (isSmallScreen) {
      setIsSidebarOpen(false);
    }

    // Focus the input field after clicking an example and highlight it
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }

      // Show highlight animation
      setHighlightInput(true);
      setTimeout(() => {
        setHighlightInput(false);
      }, 2000); // Keep highlight for 2 seconds
    }, 100);
  }

  // Handle workgroup selection change
  const handleWorkgroupChange = (e) => {
    console.log("handleWorkgroupChange: Old workgroup:", selectedWorkgroupId, "New workgroup:", e.target.value);
    setSelectedWorkgroupId(e.target.value);
    sessionIdRef.current = null;
    localStorage.removeItem('sessionId');
    setMessages([]); // Clear current messages when changing workgroups
    setIsSidebarOpen(false); // Close the sidebar
  };

  const goToAdminPage = () => {
    if (selectedWorkgroupId) {
      navigate("/admin", { state: { workgroupId: selectedWorkgroupId, userId: user.uid } });
      setIsSidebarOpen(false); // Close the sidebar
    } else {
      console.log("No workgroup selected");
    }
  };

  const goToIntegrations = () => {
    navigate("/integrations");
  };

  const goToChallenges = () => {
    navigate("/challenges");
  };

  const handleSessionSelect = async (sessionId) => {
    console.log(`handleSessionSelect called with sessionId: ${sessionId}`);
    await closeSession();
    console.log(`After closeSession, setting sessionIdRef.current = ${sessionId}`);
    sessionIdRef.current = sessionId;
    localStorage.setItem('sessionId', sessionId); // Make sure localStorage is updated

    try {
      console.log(`About to call recoverChat with sessionId=${sessionId}, userId=${user.uid}, workgroupId=${selectedWorkgroupId}`);
      await recoverChat(sessionIdRef.current, user.uid, selectedWorkgroupId);
      console.log("recoverChat completed successfully");

      // On mobile: close sidebar, on desktop: keep sidebar open
      if (isSmallScreen) {
        setIsSidebarOpen(false);
      }

      // Focus the input field after session is loaded and highlight it
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }

        // Show highlight animation
        setHighlightInput(true);
        setTimeout(() => {
          setHighlightInput(false);
        }, 2000); // Keep highlight for 2 seconds
      }, 100);

      return true; // Return success for proper Promise handling
    } catch (error) {
      console.error("Error recovering session:", error);
      alert("Failed to recover chat session: " + error.message);
      throw error; // Re-throw for proper Promise handling
    }
  };

  const handleSysPromptChange = (event) => {
    setSystemPromptInput(event)
  }

  const changeSystemPrompt = async () => {
    try {
      const response = await fetch(`${llmUrl}/v1/setSystemPrompt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Session-Id': sessionIdRef.current,  // Use sessionId from ref
        },
        body: JSON.stringify({
          session_id: sessionIdRef.current,  // Ensure sessionId is sent
          verbose: "true",
          systemPrompt: systemPromptInput,
          token: await getToken()
        }),
      });

      if (!response.ok) {
        console.error(`Server error: ${response.status}`, await response.text());
        throw new Error(`Error from server: ${response.status}`);
      }
    }
    catch (error) {
      alert("Failed to set new system prompt")
      console.log("Error setting system prompt: ", error)
    }
    console.log("Success changing prompt")
  }

  const increaseTokens = async () => {
    try {
      const response = await fetch(`${llmUrl}/v1/increaseTokens`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.uid,
          session_id: sessionIdRef.current,
          token: await getToken()
        }),
      });
      alert("Increased tokens")
    } catch (error) {
      alert("Could not increase tokens")
      console.error("Error increasing tokens:", error);
    }
  };

  const terminateRun = async () => {
    console.log("🛑 terminateRun called");
    // Mark request as cancelled to prevent response handler from overwriting
    requestCancelledRef.current = true;

    try {
      const response = await fetch(`${llmUrl}/v1/terminateRun`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.uid,
          session_id: sessionIdRef.current,
          token: await getToken()
        }),
      });

      if (response.ok) {
        // Stop loading state
        setLoadingMessage(false);
        setIsProcessingMessage(false);
        // Add a message indicating the request was stopped
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            id: prevMessages.length,
            content: "Stopped by user",
            role: "assistant",
            timestamp: Date.now(),
          },
        ]);
      } else {
        console.error("Terminate request failed:", response.status);
        requestCancelledRef.current = false; // Reset on failure
      }
    } catch (error) {
      alert("Could not terminate")
      console.error("Could not terminate:", error);
      requestCancelledRef.current = false; // Reset on error
    }
  };

  const handleResizeStart = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = internalContentWidth;

    const handleMouseMove = (e) => {
      const deltaX = startX - e.clientX;
      const newWidth = Math.max(300, Math.min(800, startWidth + deltaX));
      setInternalContentWidth(newWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const closeSession = async () => {
    console.log(`closeSession: Called with current sessionId = ${sessionIdRef.current}`);

    if (!sessionIdRef.current) {
      console.log("closeSession: No active session to close");
      return;
    }

    try {
      console.log(`closeSession: Calling API to close session ${sessionIdRef.current}`);
      const token = await getToken();
      const response = await fetch(`${llmUrl}/v1/closeSession`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.uid,
          session_id: sessionIdRef.current,
          token: token
        }),
      });

      if (response.ok) {
        console.log("closeSession: Session closed successfully");
      } else {
        console.warn(`closeSession: Server returned status ${response.status}`);
      }
    } catch (error) {
      console.warn("closeSession: Error closing session (ignored):", error);
      // Ignore failure as noted, not needed to be handled by user
    }
  }

  const deleteSession = async (sessionId) => {
    try {
      // Confirmation is now handled in Sidebar component
      // ESLint may complain about confirm() in Sidebar.tsx, but we can't fix it due to permissions
      const response = await fetch(`${llmUrl}/v1/deleteSession`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.uid,
          session_id: sessionId,
          token: await getToken()
        }),
      });

      if (response.ok) {
        // If the deleted session is the current one, start a new session
        if (sessionId === sessionIdRef.current) {
          sessionIdRef.current = null;
          localStorage.removeItem('sessionId');
          setMessages([]);

          // Initialize a new connection
          await initializeConnection();
        }
        // Refresh the sessions list
        await fetchSessions();
        return true;
      } else {
        console.error("Failed to delete session:", await response.text());
        throw new Error("Failed to delete session");
      }
    } catch (error) {
      console.error("Error deleting session:", error);
      throw error;
    }
  };

  const renameSession = async (sessionId, newName) => {
    try {
      const response = await fetch(`${llmUrl}/v1/renameSession`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.uid,
          session_id: sessionId,
          new_name: newName,
          token: await getToken()
        }),
      });

      if (response.ok) {
        // Refresh the sessions list
        await fetchSessions();
        return true;
      } else {
        console.error("Failed to rename session:", await response.text());
        throw new Error("Failed to rename session");
      }
    } catch (error) {
      console.error("Error renaming session:", error);
      throw error;
    }
  };

  // Handle prompt selection
  const handlePromptSelect = (prompt) => {
    console.log('handlePromptSelect called with prompt:', prompt);
    setInput(prompt);
    console.log('Input set to:', prompt);
    if (inputRef.current) {
      console.log('Focusing input field');
      inputRef.current.focus();
    } else {
      console.log('Input ref is not available');
    }
    // Automatically send the message
    sendMessage(prompt);
  };

  // Add function to fetch model list
  const fetchModelList = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${llmUrl}/v1/listModels?token=${token}`);

      if (!response.ok) {
        throw new Error(`Error fetching models: ${response.status}`);
      }

      const data = await response.json();
      if (data.models) {
        setModelList(data.models);
      }
    } catch (error) {
      console.error("Error fetching model list:", error);
    }
  };

  return (
    <>
      <div style={{ position: 'relative' }} className="flex flex-col min-h-full h-full bg-white dark:bg-gray-850">
        {/* Header - Fixed at the top */}
        <header className="chat-header flex-shrink-0 z-40 border-b dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex items-center w-full px-2 py-2">
            {/* Left section - Hamburger menu */}
            <div className="flex-shrink-0 mr-2">
              <button
                data-hamburger-menu="true"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent event bubbling
                  setIsSidebarOpen(!isSidebarOpen);
                }}
                className={`p-2 ${isSidebarOpen ? 'bg-blue-500 text-white' : 'bg-gray-100 dark:bg-gray-700'} rounded-lg transition-colors shadow-sm`}
                aria-label="Toggle Sidebar"
                title={isSidebarOpen ? "Hide sidebar" : "Show sidebar"}
              >
                {isSidebarOpen ? (
                  <PanelLeftClose className="w-5 h-5 text-white" />
                ) : (
                  <PanelLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                )}
              </button>
            </div>

            {/* Center section - New Chat button, Icon, Workgroup selector, and Model slider */}
            <div className="flex-1 flex items-center justify-center space-x-3 max-w-xl">
              <button
                onClick={handleNewChat}
                className="flex items-center justify-center gap-1 bg-blue-500 dark:bg-blue-600 text-white py-2 px-3 rounded-lg hover:bg-blue-600 dark:hover:bg-blue-700 transition-colors whitespace-nowrap shadow-sm"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">New Chat</span>
                <span className="sm:hidden">New</span>
              </button>


              <div className="flex-1 max-w-sm">
                <WorkgroupSelect
                  workgroups={workgroups}
                  selectedWorkgroup={selectedWorkgroupId}
                  onSelect={setSelectedWorkgroupId}
                  label={null}
                />
              </div>

              {maxModelOptions > 1 && (
                <div className="hidden md:flex items-center space-x-2 max-w-xs">
                  {isDev ? (
                    <div className="relative w-28 lg:w-36">
                      <select
                        value={modelSelection}
                        onChange={(e) => handleModelSelectionChange(parseInt(e.target.value))}
                        className="w-full px-3 py-2 text-sm border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        style={{ borderColor: '#ddd', focusRingColor: '#279DD9' }}
                      >
                        {modelList.map((model, index) => (
                          <option key={index} value={index}>
                            {model}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="relative w-28 lg:w-36">
                      <input
                        type="range"
                        min="0"
                        max={maxModelOptions - 1}
                        value={modelSelection}
                        onChange={(e) => handleModelSelectionChange(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                      />
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <span>{resolveModelName(0)}</span>
                        <span className="text-center text-xs text-gray-600 dark:text-gray-300">
                          {resolveModelName(modelSelection)} ({modelSelection + 1}/{maxModelOptions})
                        </span>
                        <span>{resolveModelName(maxModelOptions - 1)}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right section - Settings */}
            <div className="flex-shrink-0 ml-2">
              <Settings
                theme={theme}
                onThemeChange={handleThemeChange}
                showInternalContent={showInternalContent}
                onToggleInternalContent={() => setShowInternalContent(!showInternalContent)}
                isDev={isDev}
                goToAdminPage={goToAdminPage}
                onLogout={onLogout}
                sendKey={sendKey}
                onSendKeyChange={handleSendKeyChange}
                modelSelection={modelSelection}
                maxModelOptions={maxModelOptions}
                onModelSelectionChange={handleModelSelectionChange}
                ipProtection={ipProtection}
                onToggleIpProtection={handleToggleIpProtection}
                modelList={modelList}
              />
            </div>

            {/* Support button - absolutely positioned */}
            <a
              href="mailto:support@intreptos.ai"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-gray-100 dark:bg-gray-700 rounded-lg transition-colors shadow-sm hover:bg-gray-200 dark:hover:bg-gray-600"
              title="Contact Support"
              aria-label="Contact Support"
            >
              <HelpCircle className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </a>
          </div>
        </header>

        {/* Content wrapper */}
        <div className="chat-layout">
          {/* Overlay for closing sidebar when tapped outside - mobile only */}
          {isSidebarOpen && isSmallScreen && (
            <div
              className="sidebar-overlay"
              onClick={() => setIsSidebarOpen(false)}
              aria-hidden="true"
            />
          )}

          {/* Sidebar - using our new ChatGPT-style classes */}
          <div
            ref={sidebarRef}
            className={`chat-sidebar custom-scrollbar ${!isSidebarOpen ? 'chat-sidebar-hidden' : ''}`}
          >
            <Sidebar
              chats={sessions}
              selectedChat={sessionIdRef.current}
              onSelectChat={handleSessionSelect}
              onNewChat={handleNewChat}
              onDeleteChat={deleteSession}
              onRenameChat={renameSession}
              onPromptSelect={handlePromptSelect}
              theme={theme}
              user_id={user.uid}
              workgroupId={selectedWorkgroupId}
              userName={user.displayName}
              userEmail={user.email}
              onLogout={onLogout}
              goToAdminPage={goToAdminPage}
              goToIntegrations={goToIntegrations}
              goToChallenges={goToChallenges}
            />
          </div>

          {/* Main content area using our new ChatGPT-style classes */}
          <div className="chat-content">
            {/* Messages area */}
            <div className="chat-messages" ref={chatContainerRef}>
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-gray-500 dark:text-gray-400 py-6">
                  <div className="w-full max-w-3xl mb-auto px-4 mt-12">
                    <h2 className="text-xl font-medium text-center mb-6 text-gray-700 dark:text-gray-300">Start a new conversation</h2>
                  </div>

                  <div className="w-full max-w-3xl px-4 mb-28">
                    <PromptManager
                      workgroupId={selectedWorkgroupId}
                      onCardClick={sendMessage}
                    />
                  </div>
                </div>
              ) : (
                <>
                  {messages.map((message, index) => (
                    <ChatMessage
                      key={index}
                      message={message}
                      theme={theme}
                      onPromoteToPrompt={(content) => {
                        setPromptToPromote({
                          query: content,
                          title: content.substring(0, 50) + (content.length > 50 ? '...' : ''),
                          isWorkgroup: true,
                          groupId: ''
                        });

                        // Fetch groups when the modal opens
                        const fetchGroups = async () => {
                          try {
                            // Initialize default groups if none exist
                            await initializeDefaultPromptGroups(selectedWorkgroupId);

                            // Fetch workgroup groups
                            const groups = await fetchPromptGroups(selectedWorkgroupId);
                            setPromptGroups(groups);

                            // Set default group if available
                            const defaultGroup = groups.find(g => g.isDefault);
                            if (defaultGroup) {
                              setPromptToPromote(prev => ({ ...prev, groupId: defaultGroup.id }));
                            }
                          } catch (error) {
                            console.error("Error fetching prompt groups:", error);
                          }
                        };

                        fetchGroups();
                        setShowPromoteModal(true);
                      }}
                    />
                  ))}
                  {loadingMessage && <LoadingIndicator onTerminate={terminateRun} theme={theme} status={status} />}
                </>
              )}
            </div>

            {/* Chat input area - always visible */}
            <div className="chat-input-container">
              {/* Query Mode Toggle - Standard vs Direct RAG */}
              <div className="flex items-center justify-center mb-2 gap-2" data-testid="query-mode-toggle">
                <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Mode:</span>
                <div className={`inline-flex rounded-lg p-0.5 ${theme === 'dark' ? 'bg-gray-700' : 'bg-orange-200'}`}>
                  <button
                    onClick={() => setQueryMode('standard')}
                    data-testid="mode-standard-btn"
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      queryMode === 'standard'
                        ? theme === 'dark'
                          ? 'bg-green-600 text-white'
                          : 'bg-green-500 text-white'
                        : theme === 'dark'
                          ? 'text-gray-300 hover:text-white'
                          : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Standard
                  </button>
                  <button
                    onClick={() => setQueryMode('direct')}
                    data-testid="mode-direct-btn"
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      queryMode === 'direct'
                        ? theme === 'dark'
                          ? 'bg-green-600 text-white'
                          : 'bg-green-500 text-white'
                        : theme === 'dark'
                          ? 'text-gray-300 hover:text-white'
                          : 'text-gray-600 hover:text-gray-900'
                    }`}
                    title="Query knowledge base directly without AI processing"
                  >
                    Direct
                  </button>
                </div>
              </div>
              <ChatInput
                className="w-full"
                onSend={sendMessage}
                theme={theme}
                sendKey={sendKey}
                isLoading={loadingMessage}
                onCancel={terminateRun}
                inputRef={inputRef}
              />
            </div>

          </div>

          {/* Internal content panel as a separate column */}
          {showInternalContent && !isSmallScreen && (
            <div className="flex-shrink-0 relative" style={{ width: internalContentWidth }}>
              <div
                className="w-1 hover:bg-blue-500 cursor-col-resize flex items-center justify-center select-none absolute top-0 left-0 h-full hidden md:flex z-10"
                onMouseDown={handleResizeStart}
              >
                <GripVertical className="w-4 h-4 text-gray-400" />
              </div>
              <InternalContent
                width={internalContentWidth}
                messages={internal_messages}
                systemPrompt={systemPromptInput}
                onSystemPromptChange={handleSysPromptChange}
                onSubmit={changeSystemPrompt}
                maxTokens={NaN}
                onIncreaseTokens={increaseTokens}
              />
            </div>
          )}
        </div>
      </div>

      {/* Save as Prompt Modal */}
      {showPromoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium mb-4 text-gray-800 dark:text-gray-200">
              Save as Prompt Card
            </h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Prompt Title
              </label>
              <input
                type="text"
                value={promptToPromote.title}
                onChange={(e) => {
                  const newTitle = e.target.value;
                  setPromptToPromote(prev => ({ ...prev, title: newTitle }));
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Enter a title for this tile"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Query
              </label>
              <textarea
                value={promptToPromote.query}
                onChange={(e) => setPromptToPromote(prev => ({ ...prev, query: e.target.value }))}
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Edit the query if needed"
              />
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Save as
                </label>
                <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                  <button
                    onClick={async () => {
                      setPromptToPromote(prev => ({ ...prev, isWorkgroup: false, groupId: '' }));

                      // Update groups list for personal prompts
                      try {
                        await initializeDefaultPromptGroups(null);
                        const personalGroups = await fetchPromptGroups(null);
                        setPromptGroups(personalGroups);

                        // Set default group if available
                        const defaultGroup = personalGroups.find(g => g.isDefault);
                        if (defaultGroup) {
                          setPromptToPromote(prev => ({ ...prev, isWorkgroup: false, groupId: defaultGroup.id }));
                        }
                      } catch (error) {
                        console.error("Error fetching personal prompt groups:", error);
                      }
                    }}
                    className={`text-xs px-3 py-1 rounded-md transition-colors ${!promptToPromote.isWorkgroup
                        ? 'bg-blue-500 text-white'
                        : 'bg-transparent text-gray-700 dark:text-gray-300'
                      }`}
                  >
                    Personal
                  </button>
                  <button
                    onClick={async () => {
                      setPromptToPromote(prev => ({ ...prev, isWorkgroup: true, groupId: '' }));

                      // Update groups list for workgroup prompts
                      try {
                        await initializeDefaultPromptGroups(selectedWorkgroupId);
                        const workgroupGroups = await fetchPromptGroups(selectedWorkgroupId);
                        setPromptGroups(workgroupGroups);

                        // Set default group if available
                        const defaultGroup = workgroupGroups.find(g => g.isDefault);
                        if (defaultGroup) {
                          setPromptToPromote(prev => ({ ...prev, isWorkgroup: true, groupId: defaultGroup.id }));
                        }
                      } catch (error) {
                        console.error("Error fetching workgroup prompt groups:", error);
                      }
                    }}
                    className={`text-xs px-3 py-1 rounded-md transition-colors ${promptToPromote.isWorkgroup
                        ? 'bg-blue-500 text-white'
                        : 'bg-transparent text-gray-700 dark:text-gray-300'
                      }`}
                  >
                    Workgroup
                  </button>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Group
              </label>
              <select
                value={promptToPromote.groupId || ""}
                onChange={(e) => setPromptToPromote(prev => ({ ...prev, groupId: e.target.value }))}
                className="w-full px-3 py-2 text-sm border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                style={{ borderColor: '#ddd', focusRingColor: '#279DD9' }}
              >
                <option value="">Select a group</option>
                {promptGroups.map(group => (
                  <option key={group.id} value={group.id}>
                    {group.name} {group.isDefault ? "(Default)" : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowPromoteModal(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    // Create the prompt with the provided content
                    const workgroupForPrompt = promptToPromote.isWorkgroup ? selectedWorkgroupId : null;

                    const newCard = await createPromptCard({
                      title: promptToPromote.title,
                      description: '',
                      query: promptToPromote.query
                    }, workgroupForPrompt);

                    // Add to the selected group if a group was chosen
                    if (promptToPromote.groupId) {
                      await addCardToGroup(
                        promptToPromote.groupId,
                        newCard.id,
                        workgroupForPrompt
                      );
                    }

                    // Dispatch event to notify sidebar
                    window.dispatchEvent(new Event('prompt-card-created'));

                    // Close the modal and show success message
                    setShowPromoteModal(false);

                    // Show success toast message
                    const toast = document.createElement('div');
                    toast.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50';
                    toast.textContent = 'Prompt card created successfully!';
                    toast.style.transition = 'opacity 0.5s ease-in-out';
                    toast.style.opacity = '0';
                    document.body.appendChild(toast);

                    // Fade in
                    setTimeout(() => {
                      toast.style.opacity = '1';
                    }, 10);

                    // Fade out and remove after 3 seconds
                    setTimeout(() => {
                      toast.style.opacity = '0';
                      setTimeout(() => {
                        document.body.removeChild(toast);
                      }, 500);
                    }, 3000);

                    // Force a refresh of the PromptManager by triggering a re-render
                    setMessages([...messages]);
                  } catch (error) {
                    console.error('Error creating prompt card:', error);

                    // Show error toast
                    const toast = document.createElement('div');
                    toast.className = 'fixed bottom-4 right-4 bg-red-500 text-white px-4 py-2 rounded shadow-lg z-50';
                    toast.textContent = 'Failed to create prompt card. Please try again.';
                    toast.style.transition = 'opacity 0.5s ease-in-out';
                    toast.style.opacity = '0';
                    document.body.appendChild(toast);

                    // Fade in
                    setTimeout(() => {
                      toast.style.opacity = '1';
                    }, 10);

                    // Fade out and remove after 3 seconds
                    setTimeout(() => {
                      toast.style.opacity = '0';
                      setTimeout(() => {
                        document.body.removeChild(toast);
                      }, 500);
                    }, 3000);
                  }
                }}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
              >
                Save Prompt
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;
